/**
 * The axis chrome every cartesian chart in this app draws: the two axis definitions, the
 * grid box, the two ink colours they share, and the two predicates that guard them.
 *
 * Lifted out of ./chart-options.js unchanged when the site analytics report arrived as a
 * second consumer; that report now draws every chart through the shared Superset bundle and
 * takes only isFiniteNumber from here. It is not a style sheet: `categoryAxis`'s `hideOverlap`,
 * `valueAxis`'s dashed split line and `baseGrid`'s `containLabel` are the reason a long category
 * label is not clipped at the left edge and a dense axis does not overprint itself.
 *
 * Imports no echarts — these are plain objects handed to a caller that has one. Every caller
 * reaches it through a dynamically import()ed module, and keeping it echarts-free is what lets
 * SiteAnalyticsChartModulesTest assert the whole option layer stays out of the entry chunk.
 */

export const AXIS_LINE_COLOR = '#E5E7EB';
export const AXIS_LABEL_COLOR = '#6B7280';

/**
 * @param {string[]} labels  One tick per entry, already formatted by the server.
 * @param {object}   extra   Merged last, so a caller can add boundaryGap / inverse / position.
 */
export const categoryAxis = (labels, extra = {}) => ({
    type: 'category',
    data: labels,
    axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
    axisTick: { alignWithLabel: true },
    axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 11, hideOverlap: true },
    ...extra,
});

/**
 * @param {object} extra  Merged last. The metric screen passes none; a second value axis would
 *                        pass its `position` and `splitLine: {show: false}`, so two value axes do
 *                        not lay two grids over each other.
 */
export const valueAxis = (extra = {}) => ({
    type: 'value',
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: AXIS_LINE_COLOR, type: [4, 4] } },
    axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 11 },
    ...extra,
});

export const baseGrid = { left: 8, right: 16, top: 24, bottom: 8, containLabel: true };

/**
 * Whether the canvas may animate.
 *
 * The metric screen's charts animated unconditionally, including the type switch, which is
 * the largest motion on that page. The OS-level reduced-motion preference is already honoured
 * in drilldown.js, so respecting it here is consistency rather than a new convention
 * (WCAG 2.3.3).
 *
 * Read per call rather than once at module scope: the media query can flip while the page is
 * open, and every option is rebuilt on each render anyway.
 */
export const prefersMotion = () =>
    ! (typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

export const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
